import SupabasePool from "@/lib/supabaseClient";
import { useQuery } from "@tanstack/react-query";
import { useGSAP } from "@gsap/react";
import { useEffect, useRef } from "react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import gsap from "gsap";
import LoadingSpiner from "@/components/ui/loading-spiner";
import CarouselOrientation from "./components/CarouselOrientation";
import HomeSectionDescription from "./components/home-section-description";
import FadeInAnimation from "@/components/animations/FadeInAnimation";
import HomeSectionTitle from "./components/home-section-title";
import { PinnedProject } from "@/features/project/components/Project-other-item";

const RecentProject = ({ onReady }: { onReady: () => void }) => {
  const triggerRef = useRef<HTMLDivElement>(null);
  const mobileTrigger = useRef<HTMLDivElement>(null);
  const pinRef = useRef<HTMLDivElement>(null);
  const ref = useRef<HTMLDivElement>(null);

  useGSAP(
    () => {
      if (!ref.current) return;

      gsap.from(ref.current, {
        opacity: 0,
        x: 400,
        ease: "power4.out",
        duration: 1,
        scrollTrigger: {
          trigger: mobileTrigger.current,
          start: "top 70%",
          end: "center center",
          toggleActions: "play none none reverse",

          scrub: 2,
        },
      });
    },
    { scope: mobileTrigger, dependencies: [] }
  );

  const { data, isSuccess, isLoading } = useQuery<PinnedProject[]>({
    queryKey: ["project-pin"],
    queryFn: async () => {
      const { data, error } = await SupabasePool.getInstance().from(
        "project_pin"
      ).select(`
          *,
          project_meta(
            *,
            project_meta_stack(
              project_stack(*)
            )
          )
        `);
      if (error) throw new Error(error.message);
      return data;
    },
    staleTime: Infinity,
  });

  useEffect(() => {
    if (isSuccess) onReady();
  }, [isSuccess, onReady]);

  // Pin
  useGSAP(
    () => {
      if (!pinRef.current || !isSuccess || !triggerRef.current) return;

      const st = ScrollTrigger.create({
        trigger: triggerRef.current,
        start: "top 10%",
        end: () => {
          const section = triggerRef.current!;
          const pinElement = pinRef.current!;
          return `+=${section.offsetHeight - pinElement.offsetHeight}`;
        },
        pin: pinRef.current,
        pinSpacing: false,
      });

      return () => st.kill();
    },
    { scope: triggerRef, dependencies: [isSuccess] }
  );
  if (!data) return <LoadingSpiner />;
  if (data.length === 0) return null;

  return (
    <div className="flex flex-col gap-10 pt-20">
      {/* Header */}
      <FadeInAnimation>
        <HomeSectionTitle className="text-zinc-50 text-5xl leading-relaxed mb-4">
          Projects
        </HomeSectionTitle>
        <HomeSectionDescription className="text-zinc-400 break-keep leading-relaxed max-w-[600px]">
          인터랙티브 웹 퍼블리싱 경험과 React, Next.js 기반의 애플리케이션 개발
          경험을 함께 담았습니다. 기술과 사용자 경험을 함께 고민하며 작업한
          결과물입니다.
        </HomeSectionDescription>
      </FadeInAnimation>
      {/* Loading */}
      {isLoading && <LoadingSpiner />}
      {!isLoading && data && (
        <CarouselOrientation
          projectlist={data.map((e) => e.project_meta) || []}
        />
      )}{" "}
    </div>
  );
};

export default RecentProject;
